import appLogger from "../../../infra/logger/appLogger";
import { migrateLegacyServerIds } from "./rulebookDraftLegacyMigration";
import { backupCorruptDrafts, draftMemoryBacking, draftStorageForOwner, isRecord, isRulebookDraft, newDraftPublishKey, nextDraftCopyId, normalizeRulebookDraft, RULEBOOK_DRAFTS_STORAGE_KEY, } from "./rulebookDraftNormalization";
const logger = appLogger.withTag("RulebookEditor");
let stateDraftOwnerId = null;
/** The storage key of the current owner's drafts (`rulebook.editor.drafts.v1:<userId>`). */
export function rulebookDraftsStorageKey(ownerId = stateDraftOwnerId) {
    return `${RULEBOOK_DRAFTS_STORAGE_KEY}:${ownerId ?? "anonymous"}`;
}
/**
 * localStorage for a signed-in owner when the platform has it; otherwise a module-level in-memory store, so drafts
 * written without a known user never land in storage the next person on the device can read.
 */
export function resolveDraftStorage() {
    return draftStorageForOwner(stateDraftOwnerId);
}
const listeners = new Set();
const snapshotCache = new WeakMap();
let isStorageListenerAttached = false;
const notifyListeners = () => {
    listeners.forEach((listener) => listener());
};
// Another tab saving a draft changes what the Editor shows (the draft ↔ server link lives in the draft record).
const handleStorageEvent = (event) => {
    if (event.key !== null && event.key !== rulebookDraftsStorageKey()) {
        return;
    }
    snapshotCache.delete(resolveDraftStorage());
    notifyListeners();
};
const attachStorageListener = () => {
    if (isStorageListenerAttached || typeof window === "undefined") {
        return;
    }
    window.addEventListener("storage", handleStorageEvent);
    isStorageListenerAttached = true;
};
/** Subscribe to draft changes; returns the unsubscribe function. */
export function subscribeRulebookDrafts(listener) {
    attachStorageListener();
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
const persistDraftMap = (storage, drafts, ownerId) => {
    try {
        storage.setItem(rulebookDraftsStorageKey(ownerId === undefined ? stateDraftOwnerId : ownerId), JSON.stringify(drafts));
    }
    catch (error) {
        logger.warn("Rulebook drafts storage write failed; draft not persisted", error);
        return false;
    }
    snapshotCache.delete(storage);
    notifyListeners();
    return true;
};
/** Parse the stored draft map; corrupt or malformed payloads yield an empty map flagged `isCorrupt`. */
export function readRulebookDraftMap({ storage, ownerId } = {}) {
    const resolved = storage ?? resolveDraftStorage();
    const raw = resolved.getItem(rulebookDraftsStorageKey(ownerId === undefined ? stateDraftOwnerId : ownerId));
    if (raw === null) {
        return { drafts: {}, isCorrupt: false };
    }
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch (error) {
        logger.warn("Rulebook drafts storage is corrupt; starting with an empty map", error);
        return { drafts: {}, isCorrupt: true };
    }
    if (!isRecord(parsed)) {
        logger.warn("Rulebook drafts storage is not an object; starting with an empty map");
        return { drafts: {}, isCorrupt: true };
    }
    const drafts = {};
    Object.entries(parsed).forEach(([id, entry]) => {
        if (isRulebookDraft(entry)) {
            drafts[id] = normalizeRulebookDraft(entry);
        }
    });
    const hasPersisted = migrateLegacyServerIds({
        storage: resolved,
        drafts,
        draftsKey: rulebookDraftsStorageKey(ownerId === undefined ? stateDraftOwnerId : ownerId),
        ownerId: ownerId === undefined ? stateDraftOwnerId : ownerId,
    });
    if (hasPersisted) {
        snapshotCache.delete(resolved);
    }
    return { drafts, isCorrupt: false };
}
/** All drafts, most recently updated first. */
export function listRulebookDrafts({ storage } = {}) {
    const { drafts } = readRulebookDraftMap({ storage });
    return Object.values(drafts).sort((first, second) => second.updated_at.localeCompare(first.updated_at));
}
export function getRulebookDraft({ id, storage, ownerId }) {
    const { drafts } = readRulebookDraftMap({ storage, ownerId });
    return drafts[id];
}
/**
 * The `updated_at` for a write: always later than the stored stamp — same-millisecond saves would otherwise
 * share the string the conflict check compares, and a same-ms overwrite would look like no change.
 */
const nextUpdatedAt = (storedUpdatedAt) => {
    const stamped = new Date().toISOString();
    if (storedUpdatedAt === undefined || stamped > storedUpdatedAt) {
        return stamped;
    }
    const parsed = Date.parse(storedUpdatedAt);
    if (Number.isNaN(parsed)) {
        return stamped;
    }
    return new Date(parsed + 1).toISOString();
};
/** The draft record a save writes: keeps the stored publish key and server link (unless `rulebookId` overrides it) and stamps a fresh `updated_at`. */
const toStoredDraft = ({ definition, meta, rulebookId, stored, }) => {
    const linkedId = rulebookId ?? stored?.rulebook_id;
    return {
        definition,
        ...(meta ? { meta } : {}),
        publish_key: stored?.publish_key ?? newDraftPublishKey(),
        ...(linkedId !== undefined ? { rulebook_id: linkedId } : {}),
        updated_at: nextUpdatedAt(stored?.updated_at),
    };
};
/** Upsert a draft under `definition.id` and stamp `updated_at`; omitting `meta` drops any stored meta. `null` when the write fails. */
export function saveRulebookDraft({ definition, meta, rulebookId, storage }) {
    const resolved = storage ?? resolveDraftStorage();
    const { drafts, isCorrupt } = readRulebookDraftMap({ storage: resolved });
    if (isCorrupt) {
        backupCorruptDrafts({ storage: resolved, draftsKey: rulebookDraftsStorageKey() });
    }
    const draft = toStoredDraft({ definition, meta, rulebookId, stored: drafts[definition.id] });
    drafts[definition.id] = draft;
    return persistDraftMap(resolved, drafts, undefined) ? draft : null;
}
/**
 * `saveRulebookDraft` with last-writer-wins disabled: when `expectedUpdatedAt` is provided and the stored
 * draft was stamped by a different save — or was deleted since this tab last saw it — nothing is written and
 * `conflict` comes back (with the stored draft when there is one). A corrupt draft map cannot be compared
 * against, so it is backed up and overwritten rather than reported as a conflict.
 */
export function saveRulebookDraftIfUnchanged({ definition, meta, rulebookId, expectedUpdatedAt, storage, }) {
    const resolved = storage ?? resolveDraftStorage();
    const { drafts, isCorrupt } = readRulebookDraftMap({ storage: resolved });
    if (isCorrupt) {
        backupCorruptDrafts({ storage: resolved, draftsKey: rulebookDraftsStorageKey() });
    }
    const stored = drafts[definition.id];
    if (!isCorrupt && expectedUpdatedAt !== undefined && stored?.updated_at !== expectedUpdatedAt) {
        return stored !== undefined ? { status: "conflict", draft: stored } : { status: "conflict" };
    }
    const draft = toStoredDraft({ definition, meta, rulebookId, stored });
    drafts[definition.id] = draft;
    return persistDraftMap(resolved, drafts, undefined) ? { status: "saved", draft } : { status: "failed" };
}
/** The current owner's draft already linked to a published server Rulebook, if one exists. */
export function findRulebookDraftIdByServerId({ rulebookId, storage }) {
    const linked = listRulebookDrafts({ storage }).find((draft) => draft.rulebook_id === rulebookId);
    return linked?.definition.id ?? null;
}
/**
 * Records (or with `null`, forgets) the server Rulebook a draft edits — the link lives in the draft record, so
 * `updated_at` is left alone (autosave's conflict check compares it) while subscribers still get a re-read.
 * `ownerId` (default: the current owner) pins whose storage is written, so a publish that finishes after an account
 * switch still links the draft of the account that published it. `false` when the draft is missing or the write fails.
 */
export function linkRulebookDraft({ draftId, rulebookId, storage, ownerId = stateDraftOwnerId, }) {
    const resolved = storage ?? draftStorageForOwner(ownerId);
    const { drafts } = readRulebookDraftMap({ storage: resolved, ownerId });
    const draft = drafts[draftId];
    if (draft === undefined) {
        return false;
    }
    const linked = { ...draft };
    if (rulebookId === null) {
        delete linked.rulebook_id;
    }
    else {
        linked.rulebook_id = rulebookId;
    }
    drafts[draftId] = linked;
    return persistDraftMap(resolved, drafts, ownerId);
}
/** Removes a draft. `false` when storage refused the write, with the draft still there. */
export function deleteRulebookDraft({ id, storage }) {
    const resolved = storage ?? resolveDraftStorage();
    const { drafts } = readRulebookDraftMap({ storage: resolved });
    if (!(id in drafts)) {
        return true;
    }
    delete drafts[id];
    return persistDraftMap(resolved, drafts, undefined);
}
/** Clone a draft as `Copy of <name>` under `<id>-copy` (or `-copy-2`, `-copy-3`, …). `undefined` when absent or the write fails. */
export function duplicateRulebookDraft({ id, storage }) {
    const resolved = storage ?? resolveDraftStorage();
    const { drafts } = readRulebookDraftMap({ storage: resolved });
    const source = drafts[id];
    if (!source) {
        return undefined;
    }
    const copy = {
        definition: {
            ...source.definition,
            id: nextDraftCopyId(id, drafts),
            name: "Copy of " + source.definition.name,
        },
        ...(source.meta ? { meta: source.meta } : {}),
        // A copy is a new Rulebook-to-be, never a repeat of the source's first publish.
        publish_key: newDraftPublishKey(),
        updated_at: new Date().toISOString(),
    };
    drafts[copy.definition.id] = copy;
    return persistDraftMap(resolved, drafts, undefined) ? copy : undefined;
}
/**
 * Snapshot for `useSyncExternalStore`: the same reference is returned until a write
 * (or a cross-tab `storage` event) invalidates the cache.
 */
export function getRulebookDraftsSnapshot({ storage } = {}) {
    const resolved = storage ?? resolveDraftStorage();
    const key = rulebookDraftsStorageKey();
    const raw = resolved.getItem(key);
    const cached = snapshotCache.get(resolved);
    if (cached && cached.key === key && cached.raw === raw) {
        return cached.snapshot;
    }
    const { drafts, isCorrupt } = readRulebookDraftMap({ storage: resolved });
    const snapshot = {
        drafts: Object.values(drafts).sort((first, second) => second.updated_at.localeCompare(first.updated_at)),
        isCorrupt,
    };
    snapshotCache.set(resolved, { key, raw, snapshot });
    return snapshot;
}
/**
 * Scopes the draft store to the signed-in user (`null` when signed out): each account on a device keeps its own
 * drafts, and drafts are only kept in memory without one. Call it before rendering anything that reads drafts.
 */
export function setRulebookDraftOwner(ownerId) {
    const next = ownerId ? ownerId : null;
    if (next === stateDraftOwnerId) {
        return;
    }
    stateDraftOwnerId = next;
    // Drafts held in memory belong to whoever was signed in before; never carry them over.
    draftMemoryBacking.clear();
    notifyListeners();
}
/** The user the draft store is currently scoped to, `null` when signed out. */
export function getRulebookDraftOwner() {
    return stateDraftOwnerId;
}
